import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface IssueData {
  productTitle?: string;
  qcardId?: string;
  productISBN?: string;
  productModules?: any[];
  qcardTitle?: string;
  description?: string;
  questionDetails?: string;
  qcardTypeNumber?: number;
  qcardTypeAbbreviation?: string;
}

interface Issue {
  issueId: string;
  jiraId: string | null;
  crmCaseId: string;
  dataMasterQuestionId: string;
  issueStatus: string;
  crmSubject: string;
  createdAt: number;
  updatedAt: number;
  dataProductId: string;
  data?: IssueData;
  personEmail?: string;
  personFamilyName?: string;
  personGivenName?: string;
  source?: string;
}

interface IssueDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  issue: Issue | null;
}

const IssueDetailsDialog: React.FC<IssueDetailsDialogProps> = ({
  open,
  onClose,
  issue,
}) => {
  if (!issue) return null;

  const formatDate = (timestamp: number) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'CLOSED':
      case 'CLOSE':
        return 'success';
      case 'OPEN':
        return 'primary';
      default:
        return 'warning';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Issue Details
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mt: 1 }}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableBody>
                {
                  issue.issueId && (
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold', width: '40%' }}>
                        Issue ID
                      </TableCell>
                      <TableCell>{issue.issueId || 'N/A'}</TableCell>
                    </TableRow>
                  )
                }
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    JIRA ID
                  </TableCell>
                  <TableCell>{issue.jiraId || 'N/A'}</TableCell>
                </TableRow>
                {
                  issue.crmCaseId && (
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                        Case ID
                      </TableCell>
                      <TableCell>{issue.crmCaseId || 'N/A'}</TableCell>
                    </TableRow>
                  )
                }
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Subject
                  </TableCell>
                  <TableCell>{issue.crmSubject || 'N/A'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Status
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={issue.issueStatus || 'N/A'}
                      color={getStatusColor(issue.issueStatus)}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Data Master Question ID
                  </TableCell>
                  <TableCell>{issue.dataMasterQuestionId || 'N/A'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Data Product ID
                  </TableCell>
                  <TableCell>{issue.dataProductId || 'N/A'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Created At
                  </TableCell>
                  <TableCell>{formatDate(issue.createdAt)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Updated At
                  </TableCell>
                  <TableCell>{formatDate(issue.updatedAt)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Source
                  </TableCell>
                  <TableCell>{issue.source || 'N/A'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Reporter Email
                  </TableCell>
                  <TableCell>{issue.personEmail || 'N/A'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Reporter Name
                  </TableCell>
                  <TableCell>
                    {issue.personGivenName && issue.personFamilyName
                      ? `${issue.personGivenName} ${issue.personFamilyName}`
                      : issue.personGivenName || issue.personFamilyName || 'N/A'}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Data Section */}
          {issue.data && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Additional Data
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    {issue.data.productTitle && (
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold', width: '40%' }}>
                          Product Title
                        </TableCell>
                        <TableCell>{issue.data.productTitle}</TableCell>
                      </TableRow>
                    )}
                    {issue.data.qcardId && (
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                          QCard ID
                        </TableCell>
                        <TableCell>{issue.data.qcardId}</TableCell>
                      </TableRow>
                    )}
                    {issue.data.productISBN && (
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                          Product ISBN
                        </TableCell>
                        <TableCell>{issue.data.productISBN}</TableCell>
                      </TableRow>
                    )}
                    {issue.data.qcardTitle && (
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                          QCard Title
                        </TableCell>
                        <TableCell>{issue.data.qcardTitle}</TableCell>
                      </TableRow>
                    )}
                    {issue.data.description && (
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                          Description
                        </TableCell>
                        <TableCell>{issue.data.description}</TableCell>
                      </TableRow>
                    )}
                    {issue.data.questionDetails && (
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                          Question Details URL
                        </TableCell>
                        <TableCell>
                          <Typography
                            component="a"
                            href={issue.data.questionDetails}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ 
                              color: 'primary.main', 
                              textDecoration: 'underline',
                              fontSize: '0.75rem'
                            }}
                          >
                            {issue.data.questionDetails}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {issue.data.qcardTypeNumber !== undefined && (
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                          QCard Type Number
                        </TableCell>
                        <TableCell>{issue.data.qcardTypeNumber}</TableCell>
                      </TableRow>
                    )}
                    {issue.data.qcardTypeAbbreviation && (
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                          QCard Type Abbreviation
                        </TableCell>
                        <TableCell>{issue.data.qcardTypeAbbreviation}</TableCell>
                      </TableRow>
                    )}
                    {issue.data.productModules && issue.data.productModules.length > 0 && (
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                          Product Modules
                        </TableCell>
                        <TableCell>
                          {Array.isArray(issue.data.productModules)
                            ? issue.data.productModules.length > 0
                              ? JSON.stringify(issue.data.productModules)
                              : '[]'
                            : 'N/A'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IssueDetailsDialog;

